import React, { useState } from 'react';
import axios from 'axios';
import { produce } from 'immer';
import {
  useUpdateTaskResource,
  useDeleteTaskResource,
  usePrepareTaskResourceFileUpload,
  useCompleteTaskResourceUpload,
  useCancelTaskResourceUpload,
  useCreateTaskResourceLink,
  useReprocessTaskResource,
} from '../../api/endpoints/task-resources/task-resources';
import { queryClient } from '../../context/QueryClient';
import { useLecturerModuleData } from './useLecturerModuleData';
import log from '../../utils/logger';

/**
 * Hook for managing resources in the lecturer's module editor
 * Updates the module's task list cache directly since we fetch all tasks with resources
 *
 * @param {Object} task - Task object containing resources array
 */
export const useTaskResourceManagement = (task) => {
  const taskId = task?.id;
  const moduleId = task?.moduleId;
  const version = task?.version;

  const abortControllerRef = React.useRef(null);
  const uploadStartTimeRef = React.useRef(null);
  const lastProgressRef = React.useRef(null);
  const currentUploadIdRef = React.useRef(null);
  const directUploadUrlRef = React.useRef(null);
  const cancelTokenRef = React.useRef(null);
  const uploadTimeoutRef = React.useRef(null);
  // Route ids from the in-flight prepare call — cancel must use these (FLARE-6H).
  const uploadRouteRef = React.useRef({ moduleId: null, moduleVersion: null });

  const [isUploading, setIsUploading] = useState(false);
  const [reprocessingId, setReprocessingId] = useState(null);
  // Resource id -> epoch ms until which we have positive evidence a digest job is in
  // flight for that resource (just created, or just retried). Absence of a digest
  // metadata entry is NOT evidence of "still processing" once this window elapses -
  // the UI must fall back to a "not generated yet" state rather than spin forever.
  const [digestPendingUntil, setDigestPendingUntil] = useState({});

  // How long we optimistically treat a resource as "digest in flight" after
  // creation/retry. Generous margin over the typical LLM digest turnaround.
  const DIGEST_PENDING_GRACE_MS = 45000;

  const markDigestPending = React.useCallback((resourceId) => {
    if (!resourceId) return;
    const expiresAt = Date.now() + DIGEST_PENDING_GRACE_MS;
    setDigestPendingUntil((prev) => ({ ...prev, [resourceId]: expiresAt }));
    setTimeout(() => {
      setDigestPendingUntil((prev) => {
        if (prev[resourceId] !== expiresAt) return prev;
        const next = { ...prev };
        delete next[resourceId];
        return next;
      });
    }, DIGEST_PENDING_GRACE_MS);
  }, []);

  // Get module tasks query key with resources included
  const { tasksQueryKey } = useLecturerModuleData(moduleId, version, taskId);

  // Throttle helper to prevent too frequent updates
  const throttledUpdate = React.useCallback((fn) => {
    if (!lastProgressRef.current || Date.now() - lastProgressRef.current > 100) {
      fn();
      lastProgressRef.current = Date.now();
    }
  }, []);

  // Helper to update task resources in module cache using Immer for "beautiful" immutability
  const updateTaskResources = (updater) => {
    queryClient.setQueryData(tasksQueryKey, produce((draft) => {
      if (!draft?.data?.data) return;

      const targetTask = draft.data.data.find((t) => t.id === taskId);
      if (targetTask) {
        targetTask.resources = updater(targetTask.resources || []);
      }
    }));
  };

  // Helper to handle error resources with automatic removal after delay
  const handleErrorResourceRemoval = (resourceId, errorMessage) => {
    if (!resourceId) return;

    // First update the resource with the error state
    updateTaskResources((resources) => resources.map((r) => (r.id === resourceId
      ? {
        ...r,
        uploadProgress: {
          ...r.uploadProgress,
          step: 'error',
          error: errorMessage,
        },
      }
      : r)));

    // Then schedule removal after 3 seconds
    setTimeout(() => {
      // Check if this resource still exists and is still in error state
      const resources = queryClient.getQueryData(tasksQueryKey)?.data?.data?.find((t) => t.id === taskId)?.resources || [];
      const resourceToRemove = resources.find((r) => r.id === resourceId && r.uploadProgress?.step === 'error');

      if (resourceToRemove) {
        log.info('Auto-removing failed resource after delay:', resourceId);
        updateTaskResources((resources) => resources.filter((r) => r.id !== resourceId));
      }
    }, 3000);
  };

  // Step 2: Complete the upload after the file has been uploaded to Azure
  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  const completeUploadMutation = useCompleteTaskResourceUpload({
    mutation: {
      onSuccess: (response, variables) => {
        // BEAUTIFUL SURGICAL UPDATE: Use the returned resource data (which may have auto-parsed metadata)
        const updatedResource = response?.data?.data;

        if (updatedResource) {
          queryClient.setQueryData(tasksQueryKey, produce((draft) => {
            const targetTask = draft?.data?.data?.find((t) => t.id === taskId);
            if (targetTask) {
              const resourceIndex = targetTask.resources?.findIndex((r) => r.id === variables.resourceId);
              if (resourceIndex !== -1) {
                // Replace the temporary/pending resource with the server-parsed one
                targetTask.resources[resourceIndex] = updatedResource;
              }
            }
          }));
        } else {
          // Fallback to invalidation if no data returned
          queryClient.invalidateQueries({
            queryKey: tasksQueryKey,
            exact: true,
          });
        }

        // Completing the upload just kicked off the digest/thumbnail/transcript job
        // server-side (see complete_task_resource_upload) - give it a grace window
        // before treating "no digest yet" as anything other than "still working".
        markDigestPending(variables.resourceId);

        // Clean up refs
        abortControllerRef.current = null;
        uploadStartTimeRef.current = null;
        lastProgressRef.current = null;
        currentUploadIdRef.current = null;
        directUploadUrlRef.current = null;
        cancelTokenRef.current = null;
        if (uploadTimeoutRef.current) {
          clearTimeout(uploadTimeoutRef.current);
          uploadTimeoutRef.current = null;
        }

        // Set uploading to false since we're done with this upload
        setIsUploading(false);
      },
      onError: (err, variables) => {
        // Set error and schedule removal
        handleErrorResourceRemoval(variables.resourceId, err.message || 'Error completing upload');

        // Clean up timeout
        if (uploadTimeoutRef.current) {
          clearTimeout(uploadTimeoutRef.current);
          uploadTimeoutRef.current = null;
        }

        // Set uploading to false since we're done with this upload
        setIsUploading(false);
      },
    },
  });

  // Cancel upload mutation
  const cancelUploadMutation = useCancelTaskResourceUpload({
    mutation: {
      onSettled: () => {
        queryClient.invalidateQueries({
          queryKey: tasksQueryKey,
          exact: true,
        });
      },
    },
  });

  /**
   * Cancel a server-side pending resource upload.
   * Always includes moduleId + moduleVersion — omitting them produces
   * `/modules/undefined/versions/undefined/.../cancel` (FLARE-6H).
   */
  const cancelServerUpload = async (resourceId) => {
    const resolvedModuleId = uploadRouteRef.current.moduleId || moduleId;
    const resolvedModuleVersion = uploadRouteRef.current.moduleVersion || version;
    if (!resolvedModuleId || !resolvedModuleVersion || !taskId || !resourceId) {
      log.error('Skipping cancel upload: missing route ids', {
        moduleId: resolvedModuleId,
        moduleVersion: resolvedModuleVersion,
        taskId,
        resourceId,
      });
      return;
    }
    await cancelUploadMutation.mutateAsync({
      moduleId: resolvedModuleId,
      moduleVersion: resolvedModuleVersion,
      taskId,
      resourceId,
    });
  };

  // Helper to properly clean up any existing uploads before starting a new one
  const cleanupExistingUploads = async () => {
    // Find any resources that are in an uploading/preparing/completing state
    const resources = queryClient.getQueryData(tasksQueryKey)?.data?.data?.find((t) => t.id === taskId)?.resources || [];

    const uploadsInProgress = resources.filter(
      (r) => r.uploadProgress && ['preparing', 'uploading', 'completing'].includes(r.uploadProgress.step),
    );

    // Cancel each in-progress upload
    for (const resource of uploadsInProgress) {
      // Skip temporary resources that don't have real IDs yet
      if (resource.id.startsWith('temp-resource-')) {
        updateTaskResources((resources) => resources.filter((r) => r.id !== resource.id));
        // eslint-disable-next-line no-continue
        continue;
      }

      log.info(`Cancelling previous upload for resource ${resource.id}`);

      // Cancel the in-progress upload first
      if (abortControllerRef.current && abortControllerRef.current.resourceId === resource.id) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }

      // Cancel any associated axios request
      if (cancelTokenRef.current) {
        cancelTokenRef.current.cancel('Upload cancelled due to new upload starting');
        cancelTokenRef.current = null;
      }

      // Call the API to cancel the upload
      try {
        // eslint-disable-next-line no-await-in-loop
        await cancelServerUpload(resource.id);
        log.info(`Successfully cancelled upload for resource ${resource.id}`);
      } catch (error) {
        log.error(`Error cancelling upload for resource ${resource.id}:`, error);
      }

      // Remove the resource from the UI
      updateTaskResources((resources) => resources.filter((r) => r.id !== resource.id));
    }

    // Reset all refs
    if (uploadTimeoutRef.current) {
      clearTimeout(uploadTimeoutRef.current);
      uploadTimeoutRef.current = null;
    }
    uploadStartTimeRef.current = null;
    lastProgressRef.current = null;
    currentUploadIdRef.current = null;
    directUploadUrlRef.current = null;

    // Wait a tick to ensure cancellations are processed
    await new Promise((resolve) => {
      setTimeout(resolve, 0);
    });
  };

  // Step 1: Prepare a file upload by getting a direct upload URL
  const prepareFileMutation = usePrepareTaskResourceFileUpload({
    mutation: {
      onMutate: async (variables) => {
        // Store the file from the fileRef for use later
        const file = variables.fileRef?.current;

        // Set uploading state
        setIsUploading(true);
        uploadRouteRef.current = {
          moduleId: variables.moduleId,
          moduleVersion: variables.moduleVersion,
        };

        // Clean up any existing uploads first
        await cleanupExistingUploads();

        // Create new abort controller for this upload
        abortControllerRef.current = new AbortController();
        uploadStartTimeRef.current = Date.now();

        // Instead of creating an optimistic update immediately,
        // we'll create a temporary placeholder to indicate we're preparing
        // and wait for the real resource data from the API
        const temporaryId = `temp-resource-${Math.random().toString().slice(2, 8)}`;
        currentUploadIdRef.current = temporaryId;

        // Create a preparing indicator
        const preparingIndicator = {
          id: temporaryId,
          title: variables.data.title || 'Preparing upload...',
          type: 'file',
          description: variables.data.description,
          citation: variables.data.citation,
          url: null,
          uploadProgress: {
            progress: 0,
            total: file?.size || 0,
            loaded: 0,
            rate: 0,
            uploadStartTime: uploadStartTimeRef.current,
            step: 'preparing', // Status to indicate preparing upload
          },
        };

        // Update module cache with preparing indicator
        updateTaskResources((resources) => [...resources, preparingIndicator]);

        // Return both the preparing indicator and the file
        return { temporaryId, file };
      },
      onSuccess: async (response, variables, context) => {
        if (!response?.data?.data) return;

        const { resourceId, uploadUrl } = response.data.data;
        directUploadUrlRef.current = uploadUrl;

        // Get the file from context or from the variables
        const file = context.file || variables.fileRef?.current;

        if (!file) {
          log.error('No file found for upload');
          // Clean up the temporary resource
          updateTaskResources((resources) => resources.filter((r) => r.id !== context.temporaryId));

          // Since we have a real resourceId but no file, we need to cancel this resource
          try {
            await cancelServerUpload(resourceId);
          } catch (error) {
            log.error('Error cancelling upload after file not found:', error);
          }
          setIsUploading(false);
          return;
        }

        // Replace the temporary placeholder with the real resource data
        updateTaskResources((resources) => resources.map((r) => {
          if (r.id === context.temporaryId) {
            return {
              ...r,
              id: resourceId, // Replace temp ID with real one
              uploadProgress: {
                ...r.uploadProgress,
                step: 'uploading', // Update status to uploading
              },
            };
          }
          return r;
        }));

        // Start the direct upload to Azure using Axios
        try {
          // Create a cancel token for this upload
          cancelTokenRef.current = axios.CancelToken.source();

          // Create a truly clean axios instance for Azure uploads
          // By using a new import of axios (not the global one)
          const createCleanAxios = () => {
            // Import axios directly to avoid any app-wide configuration
            // eslint-disable-next-line global-require, @typescript-eslint/no-var-requires
            const freshAxios = require('axios').default.create();

            // Reset all defaults to empty objects
            freshAxios.defaults.headers = {};
            freshAxios.defaults.headers.common = {};
            freshAxios.defaults.headers.put = {};

            // Return the clean instance
            return freshAxios;
          };

          // Get a fresh axios instance with no global config
          const azureAxios = createCleanAxios();

          // Only set the specific headers needed for Azure Blob Storage
          const azureHeaders = {
            'x-ms-blob-type': 'BlockBlob',
            'Content-Type': file.type,
          };

          // Set up timeout detection as backup for network issues
          uploadTimeoutRef.current = setTimeout(() => {
            // Only trigger if the upload is still in progress
            if (cancelTokenRef.current && resourceId) {
              log.warn('Upload timeout detected after 60 seconds of inactivity');

              // Cancel the upload
              if (cancelTokenRef.current) {
                cancelTokenRef.current.cancel('Upload timed out');
                cancelTokenRef.current = null;
              }

              // Mark as error and clean up
              try {
                cancelServerUpload(resourceId)
                  .catch((err) => {
                    log.error('Error cancelling upload after timeout:', err);
                  });

                handleErrorResourceRemoval(resourceId, 'Upload timed out. Azure storage may be unavailable.');
              } catch (error) {
                log.error('Error handling upload timeout:', error);
              }
              setIsUploading(false);
            }
          }, 65000); // Slightly longer than the axios timeout

          // Configure the upload
          const uploadConfig = {
            onUploadProgress: (progressEvent) => {
              if (!resourceId) return;

              // Reset the activity timeout on progress
              if (uploadTimeoutRef.current) {
                clearTimeout(uploadTimeoutRef.current);
                uploadTimeoutRef.current = setTimeout(() => {
                  // Only trigger if the upload is still not complete
                  if (cancelTokenRef.current && resourceId) {
                    log.warn('Upload stalled - no progress after 60 seconds');

                    if (cancelTokenRef.current) {
                      cancelTokenRef.current.cancel('Upload stalled');
                      cancelTokenRef.current = null;
                    }

                    try {
                      cancelServerUpload(resourceId)
                        .catch((err) => {
                          log.error('Error cancelling stalled upload:', err);
                        });

                      handleErrorResourceRemoval(resourceId, 'Upload stalled. Network or storage issue detected.');
                    } catch (error) {
                      log.error('Error handling stalled upload:', error);
                    }
                    setIsUploading(false);
                  }
                }, 65000);
              }

              throttledUpdate(() => {
                const { loaded } = progressEvent;
                const { total } = progressEvent;
                const progress = loaded / total;
                const timeElapsed = (Date.now() - uploadStartTimeRef.current) / 1000; // seconds
                const rate = timeElapsed > 0 ? loaded / timeElapsed : 0; // bytes per second

                // Update the resource with progress
                updateTaskResources((resources) => resources.map((r) => (r.id === resourceId
                  ? {
                    ...r,
                    uploadProgress: {
                      progress,
                      total,
                      loaded,
                      rate,
                      uploadStartTime: uploadStartTimeRef.current,
                      step: 'uploading',
                    },
                  }
                  : r)));
              });
            },
            cancelToken: cancelTokenRef.current.token,
          };

          // Store resourceId for potential cancel operations
          abortControllerRef.current = {
            abort: () => {
              if (cancelTokenRef.current) {
                cancelTokenRef.current.cancel('Upload cancelled by user');
              }
              if (uploadTimeoutRef.current) {
                clearTimeout(uploadTimeoutRef.current);
                uploadTimeoutRef.current = null;
              }
            },
            resourceId,
          };

          // Execute the direct upload to Azure with the clean axios instance
          try {
            // Execute with minimal configuration
            await azureAxios.put(uploadUrl, file, {
              headers: azureHeaders,
              timeout: 1000 * 60 * 30, // 30 minutes
              onUploadProgress: uploadConfig.onUploadProgress,
              cancelToken: cancelTokenRef.current.token,
            });

            // Clear the timeout since upload completed successfully
            if (uploadTimeoutRef.current) {
              clearTimeout(uploadTimeoutRef.current);
              uploadTimeoutRef.current = null;
            }

            // Upload completed successfully
            updateTaskResources((resources) => resources.map((r) => (r.id === resourceId
              ? {
                ...r,
                uploadProgress: {
                  ...r.uploadProgress,
                  progress: 1,
                  step: 'completing',
                },
              }
              : r)));

            // Complete the upload by notifying the backend
            try {
              await completeUploadMutation.mutateAsync({
                moduleId: variables.moduleId,
                moduleVersion: variables.moduleVersion,
                taskId,
                resourceId,
              });

              // No need to set isUploading to false here as it's done in completeUploadMutation.onSuccess
            } catch (error) {
              log.error('Error completing upload:', error);

              // We need to ensure we call the cancel endpoint since the file was uploaded
              // but the completion failed
              try {
                await cancelServerUpload(resourceId);
                log.info('Resource cancelled after completion failure');
              } catch (cancelError) {
                log.error('Error cancelling upload after completion failure:', cancelError);
              }

              // Set error and schedule removal
              handleErrorResourceRemoval(resourceId, error.message || 'Error completing upload');

              // Set uploading to false since we're done with this upload
              setIsUploading(false);
            }
          } catch (error) {
            // Skip showing error for cancelled uploads
            if (axios.isCancel(error)) {
              log.info('Upload cancelled:', error.message);
              setIsUploading(false);
              return;
            }

            log.error('Error during direct upload setup:', error);

            // Clear the timeout
            if (uploadTimeoutRef.current) {
              clearTimeout(uploadTimeoutRef.current);
              uploadTimeoutRef.current = null;
            }

            // We need to ensure we call the cancel endpoint since the setup failed
            try {
              await cancelServerUpload(resourceId);
              log.info('Resource cancelled after upload setup failure');
            } catch (cancelError) {
              log.error('Error cancelling upload after setup failure:', cancelError);
            }

            // Set error and schedule removal
            handleErrorResourceRemoval(resourceId, error.message || 'Error during upload setup');

            // Set uploading to false since we're done with this upload
            setIsUploading(false);
          } finally {
            // Clean up cancel token and timeout
            cancelTokenRef.current = null;
            if (uploadTimeoutRef.current) {
              clearTimeout(uploadTimeoutRef.current);
              uploadTimeoutRef.current = null;
            }
          }
        } catch (error) {
          // Skip showing error for cancelled uploads
          if (axios.isCancel(error)) {
            log.info('Upload cancelled:', error.message);
            setIsUploading(false);
            return;
          }

          log.error('Error during direct upload setup:', error);

          // Clear the timeout
          if (uploadTimeoutRef.current) {
            clearTimeout(uploadTimeoutRef.current);
            uploadTimeoutRef.current = null;
          }

          // We need to ensure we call the cancel endpoint since the setup failed
          try {
            await cancelServerUpload(resourceId);
            log.info('Resource cancelled after upload setup failure');
          } catch (cancelError) {
            log.error('Error cancelling upload after setup failure:', cancelError);
          }

          // Set error and schedule removal
          handleErrorResourceRemoval(resourceId, error.message || 'Error during upload setup');

          // Set uploading to false since we're done with this upload
          setIsUploading(false);
        }
      },
      onError: (err, variables, context) => {
        // FLARE-8M: prepare 400 is usually an expected filetype rejection
        // (resources_allowed_filetypes). Surface the API detail on the temp
        // row so the lecturer sees why the upload failed, and avoid Sentry
        // for client validation 400s (Failure-Surface: signal + recovery).
        const status = err?.response?.status;
        const detail = typeof err?.response?.data?.detail === 'string'
          ? err.response.data.detail
          : null;
        const message = detail || err?.message || 'Error preparing upload';

        if (status === 400) {
          log.warn('Prepare file upload rejected:', message);
        } else {
          log.error('Error in prepare file upload:', err);
        }

        if (context?.temporaryId) {
          handleErrorResourceRemoval(context.temporaryId, message);
        }

        // Clean up all refs
        abortControllerRef.current = null;
        uploadStartTimeRef.current = null;
        lastProgressRef.current = null;
        currentUploadIdRef.current = null;
        directUploadUrlRef.current = null;
        cancelTokenRef.current = null;
        if (uploadTimeoutRef.current) {
          clearTimeout(uploadTimeoutRef.current);
          uploadTimeoutRef.current = null;
        }

        // Set uploading to false since we're done with this upload
        setIsUploading(false);
      },
    },
  });

  // Create link resource mutation
  const createLinkMutation = useCreateTaskResourceLink({
    mutation: {
      onMutate: async (variables) => {
        const optimisticResource = {
          id: `temp-resource-${Math.random().toString().slice(2, 8)}`,
          title: variables.data.title || 'New Link',
          type: 'link',
          description: variables.data.description,
          citation: variables.data.citation,
          url: variables.data.url, // Using variables.data.url from the new schema
          uploadProgress: variables.data.autoGenerateMetadata ? {
            progress: 0.5,
            step: 'analyzing_link', // New status for link analysis
          } : null,
        };

        // Update module cache
        updateTaskResources((resources) => [...resources, optimisticResource]);

        return { optimisticResource };
      },
      onSuccess: (response, variables, context) => {
        const updatedResource = response?.data?.data;

        if (updatedResource) {
          queryClient.setQueryData(tasksQueryKey, produce((draft) => {
            const targetTask = draft?.data?.data?.find((t) => t.id === taskId);
            if (targetTask) {
              const resourceIndex = targetTask.resources?.findIndex((r) => r.id === context.optimisticResource.id);
              if (resourceIndex !== -1) {
                // Replace the temporary/pending resource with the server-parsed one
                targetTask.resources[resourceIndex] = updatedResource;
              }
            }
          }));

          // Link creation kicks off the digest job server-side (see
          // create_task_resource_link) - track the real (non-temp) id so the
          // grace window survives the settle-time refetch below.
          markDigestPending(updatedResource.id);
        }
      },
      onError: (err, variables, context) => {
        // Revert optimistic update
        if (context?.optimisticResource) {
          updateTaskResources((resources) => resources.filter((r) => r.id !== context.optimisticResource.id));
        }
      },
      onSettled: () => {
        // Invalidate to ensure everything is in sync, though surgical update should handle UI
        queryClient.invalidateQueries({
          queryKey: tasksQueryKey,
          exact: true,
        });
      },
    },
  });

  const updateMutation = useUpdateTaskResource({
    mutation: {
      onMutate: (variables) => {
        const updatedResource = variables.data;

        // Check if this is a position-only update
        const isPositionOnlyUpdate = Object.keys(updatedResource).length === 1 && updatedResource.position !== undefined;

        updateTaskResources((resources) => resources.map((r) => {
          if (r.id === variables.resourceId) {
            if (isPositionOnlyUpdate) {
              // For position-only updates, preserve the existing position (from move function's optimistic update)
              // and update everything else
              return { ...r, ...updatedResource, position: r.position };
            }
            // For regular updates, update everything including position
            return { ...r, ...updatedResource };
          }
          return r;
        }));
      },
      onSettled: () => {
        // Don't invalidate during file uploads to prevent interruption
        if (!currentUploadIdRef.current) {
          queryClient.invalidateQueries({
            queryKey: tasksQueryKey,
            exact: true,
          });
        }
      },
    },
  });

  const deleteMutation = useDeleteTaskResource({
    mutation: {
      onMutate: (variables) => {
        updateTaskResources((resources) => resources.filter((r) => r.id !== variables.resourceId));
      },
      onSettled: () => {
        queryClient.invalidateQueries({
          queryKey: tasksQueryKey,
          exact: true,
        });
      },
    },
  });

  // Reprocess (rebuild the AI digest) for a single resource. The digest is regenerated
  // in the background, so we optimistically mark the resource as reprocessing and refetch
  // a couple of times to pick up the new outcome (digest ready / failed).
  const reprocessMutation = useReprocessTaskResource({
    mutation: {
      onMutate: (variables) => {
        setReprocessingId(variables.resourceId);
        markDigestPending(variables.resourceId);
        updateTaskResources((resources) => resources.map((r) => (r.id === variables.resourceId
          ? { ...r, reprocessing: true }
          : r)));
      },
      onError: (err, variables) => {
        log.error('Error reprocessing resource:', err);
        updateTaskResources((resources) => resources.map((r) => (r.id === variables.resourceId
          ? { ...r, reprocessing: false }
          : r)));
        setReprocessingId(null);
      },
      onSuccess: () => {
        const refetch = () => queryClient.invalidateQueries({ queryKey: tasksQueryKey, exact: true });
        // Digest generation is async (LLM call); poll-by-refetch on a short, then longer delay.
        setTimeout(refetch, 8000);
        setTimeout(() => {
          refetch();
          setReprocessingId(null);
        }, 20000);
      },
    },
  });

  const reprocessResource = (resourceId, mId, mVersion) => reprocessMutation.mutateAsync({
    moduleId: mId,
    moduleVersion: mVersion,
    taskId,
    resourceId,
  });

  // Add cancel upload function
  const cancelUpload = async () => {
    if (abortControllerRef.current) {
      // First abort the in-progress request
      abortControllerRef.current.abort();

      // Cancel any active axios request
      if (cancelTokenRef.current) {
        cancelTokenRef.current.cancel('Upload cancelled by user');
        cancelTokenRef.current = null;
      }

      // Clear any timeout
      if (uploadTimeoutRef.current) {
        clearTimeout(uploadTimeoutRef.current);
        uploadTimeoutRef.current = null;
      }

      // If we have a real resource ID (not just an optimistic one), call API to cancel it
      if (abortControllerRef.current.resourceId && !abortControllerRef.current.resourceId.startsWith('temp-')) {
        try {
          await cancelServerUpload(abortControllerRef.current.resourceId);
        } catch (error) {
          log.error('Error cancelling upload:', error);
        }
      } else if (currentUploadIdRef.current) {
        updateTaskResources((resources) => resources.filter((r) => r.id !== currentUploadIdRef.current));
      }

      // Reset all refs
      await new Promise((resolve) => {
        setTimeout(resolve, 0);
      });
      abortControllerRef.current = null;
      currentUploadIdRef.current = null;
      uploadStartTimeRef.current = null;
      lastProgressRef.current = null;
      directUploadUrlRef.current = null;

      // Set uploading to false
      setIsUploading(false);
    }
  };

  // Combined createMutation function that chooses the right mutation based on input
  const createMutation = {
    mutate: (variables, options = {}) => {
      if (variables.data.file) {
        // For file uploads, start with preparing the upload
        const { file } = variables.data;
        const { file: unusedFile, ...metadata } = variables.data;

        // Store the file in a variable that will persist through the promise chain
        // This ensures we have access to the actual file in onSuccess
        const fileRef = { current: file };

        prepareFileMutation.mutate(
          {
            moduleId: variables.moduleId,
            moduleVersion: variables.moduleVersion,
            taskId: variables.taskId,
            data: {
              filename: file.name,
              content_type: file.type,
              filesize: file.size,
              ...metadata,
            },
            fileRef, // Pass the file reference along with the mutation
          },
          {
            onSuccess: (data, variables) => {
              // Call the original onSuccess if provided
              if (options.onSuccess) {
                options.onSuccess(data, variables);
              }
            },
            onError: (error, variables) => {
              log.error('Error preparing file upload:', error);
              // Call the original onError if provided
              if (options.onError) {
                options.onError(error, variables);
              }
            },
          },
        );
      } else {
        // For links, use createLinkMutation
        createLinkMutation.mutate(
          {
            moduleId: variables.moduleId,
            moduleVersion: variables.moduleVersion,
            taskId: variables.taskId,
            data: variables.data,
          },
          options,
        );
      }
    },
    mutateAsync: async (variables, options = {}) => {
      if (variables.data.file) {
        // For file uploads, start with preparing the upload
        const { file } = variables.data;
        const { file: unusedFile, ...metadata } = variables.data;

        // Store the file in a variable that will persist through the promise chain
        const fileRef = { current: file };

        return prepareFileMutation.mutateAsync(
          {
            moduleId: variables.moduleId,
            moduleVersion: variables.moduleVersion,
            taskId: variables.taskId,
            data: {
              filename: file.name,
              content_type: file.type,
              filesize: file.size,
              ...metadata,
            },
            fileRef,
          },
          options,
        );
      }
      // For links, use createLinkMutation
      return createLinkMutation.mutateAsync(
        {
          moduleId: variables.moduleId,
          moduleVersion: variables.moduleVersion,
          taskId: variables.taskId,
          data: {
            ...variables.data,
            url: variables.data.url || variables.data.link_url, // Support both for backward compatibility during transition
          },
        },
        options,
      );
    },
    isLoading: prepareFileMutation.isLoading || createLinkMutation.isLoading || completeUploadMutation.isLoading,
    isError: prepareFileMutation.isError || createLinkMutation.isError || completeUploadMutation.isError,
    error: prepareFileMutation.error || createLinkMutation.error || completeUploadMutation.error,
    reset: () => {
      prepareFileMutation.reset();
      createLinkMutation.reset();
      completeUploadMutation.reset();
    },
  };

  // Helper function to get sorted resources by position
  const getSortedResources = (resources) => [...resources].sort((a, b) => {
    // If both have position, sort by position
    if (a.position !== null && a.position !== undefined && b.position !== null && b.position !== undefined) {
      return a.position - b.position;
    }
    // If only a has position, it comes first
    if (a.position !== null && a.position !== undefined) return -1;
    // If only b has position, it comes first
    if (b.position !== null && b.position !== undefined) return 1;
    // If neither has position, maintain original order
    return 0;
  });

  // Move resource up function
  const moveResourceUp = (resourceId, moduleId, moduleVersion) => {
    const resources = queryClient.getQueryData(tasksQueryKey)?.data?.data?.find((t) => t.id === taskId)?.resources || [];

    const sortedResources = getSortedResources(resources);
    const currentIndex = sortedResources.findIndex((r) => r.id === resourceId);

    if (currentIndex <= 0) return; // Already at the top

    // The server wants the on-screen row, not the neighbor's stored position.
    const targetIndex = currentIndex - 1;
    const neighbor = sortedResources[targetIndex];
    const neighborPosition = neighbor.position ?? targetIndex;

    updateTaskResources((resources) => resources.map((r) => (r.id === resourceId ? { ...r, position: neighborPosition - 0.1 } : r)));

    updateMutation.mutate({
      moduleId,
      moduleVersion,
      taskId,
      resourceId,
      data: { position: targetIndex },
    });
  };

  // Move resource down function
  const moveResourceDown = (resourceId, moduleId, moduleVersion) => {
    const resources = queryClient.getQueryData(tasksQueryKey)?.data?.data?.find((t) => t.id === taskId)?.resources || [];

    const sortedResources = getSortedResources(resources);
    const currentIndex = sortedResources.findIndex((r) => r.id === resourceId);

    if (currentIndex >= sortedResources.length - 1) return; // Already at the bottom

    const targetIndex = currentIndex + 1;
    const neighbor = sortedResources[targetIndex];
    const neighborPosition = neighbor.position ?? targetIndex;

    updateTaskResources((resources) => resources.map((r) => (r.id === resourceId ? { ...r, position: neighborPosition + 0.1 } : r)));

    updateMutation.mutate({
      moduleId,
      moduleVersion,
      taskId,
      resourceId,
      data: { position: targetIndex },
    });
  };

  return {
    createMutation,
    updateMutation,
    deleteMutation,
    cancelUpload,
    isUploading,
    reprocessResource,
    reprocessingId,
    digestPendingUntil,
    moveResourceUp,
    moveResourceDown,
    getSortedResources,
    isUpdating: updateMutation.isPending,
  };
};
